import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { collectNormalizedReportRisks } from '../../../../lib/sectionFallbacks/derivedFallbacks';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import {
  EditorialBadge,
  EditorialDataTable,
  type EditorialBadgeTone,
} from '../../report/editorial';

/** Risk severity → themed badge tone (replaces the filled RAG dot). */
function severityTone(severity: string | undefined): EditorialBadgeTone {
  if (severity === 'high' || severity === 'critical') return 'negative';
  if (severity === 'medium') return 'warning';
  return 'neutral';
}

/**
 * Layout risk matrix — surfaces report.risks when present; otherwise derives
 * rows from risks + developmentRisks (A6).
 *
 * W1c editorial port (14B ranked register) — the accent-dot <ul> and the
 * raw markdown <pre> both become an EditorialDataTable register
 * (Risk × Severity × Impact/Detail) with EditorialBadge severity cells.
 * The derived path keeps deriveRiskMatrix's exact shaping (same
 * collectNormalizedReportRisks source, first 8 risks, title ≤ 80 chars,
 * detail ≤ 140 chars) so the register carries the same content the
 * markdown table did. The report carries NO likelihood × impact ordinals
 * (GAP — gen-side), so the 14A matrix stays out of reach here; the 14B
 * register is the documented fallback. Never assign random L/I.
 */
export const RiskMatrixSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const risks = report.risks ?? [];
  // Derived fallback (A6) — same normalization deriveRiskMatrix renders,
  // only consulted when report.risks is empty (parity with the legacy
  // markdown-table path, including its slice/truncation limits).
  const derivedRows = risks.length === 0
    ? collectNormalizedReportRisks(report).slice(0, 8).map((r) => ({
        label: (r.title ?? '').slice(0, 80),
        severity: r.severity ?? 'medium',
        detail: (r.description ?? '').slice(0, 140),
      }))
    : [];
  const hasData = risks.length > 0 || derivedRows.length > 0;

  const rows: React.ReactNode[][] = risks.length > 0
    ? risks.map((r) => [
        r.label,
        r.severity ? (
          <EditorialBadge theme={theme} tone={severityTone(r.severity)} variant="outline">
            {r.severity}
          </EditorialBadge>
        ) : null,
        r.impact,
      ])
    : derivedRows.map((r) => [
        r.label,
        <EditorialBadge theme={theme} tone={severityTone(r.severity)} variant="outline">
          {r.severity}
        </EditorialBadge>,
        r.detail,
      ]);

  return (
    <SectionShell label={label} hasData={hasData} report={report} sectionId="riskMatrix" required>
      {hasData && (
        // Carrier div — EditorialDataTable doesn't take a data-testid.
        <div data-testid="test-fit-risk-register">
          <EditorialDataTable
            theme={theme}
            columns={[
              { header: 'Risk' },
              { header: 'Severity', width: 110 },
              { header: risks.length > 0 ? 'Impact' : 'Detail' },
            ]}
            rows={rows}
          />
        </div>
      )}
    </SectionShell>
  );
};
